import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './MobileMenu.css';

interface MobileMenuProps {
    isOpen: boolean;
    onClose: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose }) => {
    const location = useLocation();
    const prevPathnameRef = useRef(location.pathname);

    const isActive = (path: string) => location.pathname === path;

    // Close menu on escape key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    // Prevent scroll when menu is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
    }, [isOpen]);

    // Close menu ONLY when the route (pathname) actually changes
    useEffect(() => {
        const prev = prevPathnameRef.current;
        const next = location.pathname;

        if (prev !== next) {
            if (isOpen) onClose();
            prevPathnameRef.current = next;
        }
    }, [location.pathname, isOpen, onClose]);

    const ArrowIcon = () => (
        <span className="menu-arrow">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
            </svg>
        </span>
    );

    return (
        <div className={`mobile-menu-overlay ${isOpen ? 'open' : ''}`}>
            <div className="mobile-menu-header">
                <Link to="/" className="mobile-menu-logo">
                    <div className="logo-container">
                        <img
                            src="/favicon-modli.png"
                            alt="Modli 3D"
                        />
                        <span className="logo-text">MODLI 3D</span>
                    </div>
                </Link>
                <div className="mobile-menu-close" onClick={onClose}>
                    <span className="close-text">Close</span>
                    <div className="close-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </div>
                </div>
            </div>

            <div className="mobile-menu-inner">
                <div className="mobile-menu-main">
                    <ul className="mobile-menu-nav">
                        <li>
                            <Link to="/" className={isActive('/') ? 'active' : ''}>Home <ArrowIcon /></Link>
                        </li>
                        <li>
                            <Link to="/about-us" className={isActive('/about-us') ? 'active' : ''}>About Us<ArrowIcon /></Link>
                        </li>
                        <li>
                            <Link to="/shop" className={isActive('/shop') ? 'active' : ''}>Shop<ArrowIcon /></Link>
                        </li>
                        <li>
                            <Link to="/contact" className={isActive('/contact') ? 'active' : ''}>Contact Us <ArrowIcon /></Link>
                        </li>
                    </ul>
                </div>
            </div>

            <div className="mobile-menu-footer">
                <div className="socials-mobile">
                    <a
                        href="https://www.facebook.com/share/1F4rX7FHd9/"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Facebook"
                        className="social-btn"
                    >
                        <i className="icon-facebook-1"></i>
                    </a>
                    <a
                        href="https://www.instagram.com/modli3d?igsh=YnFpbWJrNHY1ZzVu"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Instagram"
                        className="social-btn"
                    >
                        <i className="icon-instagram"></i>
                    </a>
                </div>
            </div>
        </div>
    );
};

export default MobileMenu;
